<template>
  <div>
    <StandaloneWpEdit
      v-if="editView"
    />
    <div
      v-if="!editView"
      class="standalone-wp-view-container"
    >
      <Sticky
        class="stick-btnGroup"
        extendStickyClass="stickyBtnClass"
        :fullWidth="true"
        :align="batch ? 'right' : 'left'"
      >
        <div class="back-button">
          <HKLHeader
            v-if="navEntries.length"
            :labelLeftPrimary="$t('listNavigation.backToDashboard')"
            :labelRightLeading="$t('listNavigation.previousRecord')"
            :labelRightTrailing="$t('listNavigation.nextRecord')"
            :navigationEntries="navEntries"
            :currentRecordId="currentRecordId"
            :navigatePrevEnabled="batch ? canPrev : undefined"
            :navigateNextEnabled="batch ? canNext : undefined"
            @left-primary="navToWorkPermitDashboard"
            @right-leading="prev"
            @right-trailing="next"
          />
          <span
            v-else-if="!batch"
            class="back"
            @click="back()"
          >
            &lt; {{ $t('back') }}
          </span>
        </div>
      </Sticky>

      <WorkPermitDetail
        :isProjectAvailable="true"
        :focusPermitId="focusPermitId"
        :isPPSuspened="isPPSuspened"
      />
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import Sticky from '@/controls/Sticky'
import HKLHeader from '@/controls/HKLHeader'
import mutationTypes from '@/store/mutation-types'
import { getBuildingFloorZoneMap } from '@/api/buildings/request'
import { navigate } from '@/api/workPermit/util'
import { LIST_NAV_KIND, pushListNavigationEntry, resolveNavigationKind } from '@/constants/listNavigation'
import { orderByPermitCode } from '@/utils/sortingHelper'
import navigation from '@/utils/navigation'
import StandaloneWpEdit from './StandaloneWpEdit'
import WorkPermitDetail from './WorkPermitDetail'
import { WORK_PERMIT_STATUS, WORK_PERMIT_TYPES } from './consts'

export default {
  name: 'StandaloneWp',
  components: {
    StandaloneWpEdit,
    HKLHeader,
    Sticky,
    WorkPermitDetail,
  },
  beforeRouteEnter (to, from, next) {
    next(vm => vm.setFromPage(from.name))
  },
  data () {
    const workPermitId = this.$route.params.id
    const hoistingId = this.$route.query.hoistingId
    const focusPermitId = hoistingId || workPermitId
    return {
      workPermitId,
      focusPermitId,
      fromPage: '',
      changeToEdit: false,
    }
  },
  computed: {
    ...mapGetters('workPermit', ['currentWorkPermit', 'isUnderBatch']),
    ...mapGetters('listNavigation', ['listNavigationSequence']),
    ...mapGetters('user', ['isEngineer']),
    visible () {
      if (!this.currentWorkPermit) return false
      if (this.currentWorkPermit.id !== this.workPermitId) return false
      return true
    },
    isDraft () {
      return lodash.get(this, 'currentWorkPermit.status') === WORK_PERMIT_STATUS.DRAFT
    },
    editView () {
      return this.visible && (this.isDraft || this.changeToEdit)
    },
    isPPSuspened () {
      return lodash.get(this, 'currentWorkPermit.status') === WORK_PERMIT_STATUS.SUSPENDED
    },
    currentRecordId () {
      return String(this.$route.query.hoistingId || this.$route.params.id || '')
    },
    batch () {
      return this.isUnderBatch || String(lodash.get(this, '$route.query.underBatch', '')) === 'true'
    },
    listNav () {
      const seq = this.listNavigationSequence || []
      if (!seq.length) return []
      return seq.every(e => resolveNavigationKind(e) === LIST_NAV_KIND.WORK_PERMIT) ? seq : []
    },
    permitSeq () {
      if (!this.currentWorkPermit || !this.currentWorkPermit.id) return []
      return [
        this.currentWorkPermit,
        ...orderByPermitCode(lodash.get(this, 'currentWorkPermit.hoistings', [])),
      ].filter(permit => {
        return !(permit.status === WORK_PERMIT_STATUS.DRAFT && this.isEngineer)
      })
    },
    navEntries () {
      if (this.hasWorkPermitListNavigation) return this.listNav
      if (this.batch) {
        return this.permitSeq
          .filter(permit => permit && permit.id)
          .map(permit => ({ id: permit.id, kind: LIST_NAV_KIND.WORK_PERMIT }))
      }
      return []
    },
    hasWorkPermitListNavigation () {
      const seq = this.listNav
      const cur = this.currentRecordId
      return !!(seq.length && cur && seq.some(e => String(e.id) === cur))
    },
    canPrev () {
      if (this.hasWorkPermitListNavigation) return !!this.listEntry(-1)
      return !!this.permitAt(-1)
    },
    canNext () {
      if (this.hasWorkPermitListNavigation) return !!this.listEntry(1)
      return !!this.permitAt(1)
    },
  },
  watch: {
    '$route.params.id': async function (newId, oldId) {
      if (newId && oldId && (newId !== oldId)) {
        await this.init(newId)
      }
    },
    '$route.query.hoistingId' (val) {
      this.hoistingId = val
      this.focusPermitId = val || this.workPermitId
    },
    '$route.query.edit' (val) {
      this.changeToEdit = val === 'Y'
    },
  },
  async mounted () {
    window.scrollTo(0, 0)
    this.changeToEdit = this.$route.query.edit === 'Y'
    await this.init()
  },
  methods: {
    ...mapActions('ccd', ['loadTenancy']),
    ...mapActions('workPermit', ['getWPByIdSkipCache']),
    ...mapMutations('workPermit', {
      setBuildingFloorMap: mutationTypes.WORKPERMIT_SET_BUILDING_FLOOR_MAP,
    }),
    ...mapMutations('workPermit', {
      updateFieldByName: mutationTypes.WORKPERMIT_SET_WORKPERMIT_FIELD_BY_NAME,
    }),
    async init (workPermitId) {
      await this.getWPByIdSkipCache(workPermitId || this.workPermitId)
      const buildingsInfo = await getBuildingFloorZoneMap()
      this.setBuildingFloorMap(buildingsInfo.BuildingFloorZoneMap)
      this.workPermitId = this.$route.params.id
      this.hoistingId = this.$route.query.hoistingId
      this.focusPermitId = this.hoistingId || this.workPermitId
    },
    setFromPage (name) {
      this.fromPage = name
    },
    back () {
      this.$router.goBack()
    },
    navToWorkPermitDashboard () {
      navigation.toPage('workPermitDashboard')
    },
    listEntry (offset) {
      const index = this.listNav.findIndex(e => String(e.id) === this.currentRecordId)
      if (index < 0) return null
      return this.listNav[index + offset] || null
    },
    permitAt (offset) {
      const currentId = this.$route.query.hoistingId || this.$route.params.id
      const sequence = this.permitSeq || []
      const index = sequence.findIndex(permit => String(permit.id) === String(currentId))
      if (index < 0) return null
      const permit = sequence[index + offset] || null
      if (
        this.batch &&
        offset < 0 &&
        this.$route.query.hoistingId &&
        permit &&
        String(permit.id) === String(this.$route.params.id)
      ) {
        return null
      }
      return permit
    },
    goPermit (permit) {
      const hoistingId = permit.type === WORK_PERMIT_TYPES.HOISTING ? permit.id : undefined
      this.focusPermitId = hoistingId || permit.id
      navigate(permit, hoistingId, { fromViewAll: false, underBatch: true })
    },
    prev (entry) {
      if (this.hasWorkPermitListNavigation) {
        this.pushNav(entry || this.listEntry(-1))
        return
      }
      if (this.batch) {
        const permit = this.permitAt(-1)
        if (permit) {
          this.goPermit(permit)
          return
        }
      }
      this.pushNav(entry)
    },
    next (entry) {
      if (this.hasWorkPermitListNavigation) {
        this.pushNav(entry || this.listEntry(1))
        return
      }
      if (this.batch) {
        const permit = this.permitAt(1)
        if (permit) {
          this.goPermit(permit)
          return
        }
      }
      this.pushNav(entry)
    },
    pushNav (entry) {
      pushListNavigationEntry(this.$router, this.$route, {}, entry)
    },
  },
}
</script>

<style lang="scss" scoped>
  .standalone-wp-view-container {
    max-width: 1440px;
    margin: 0 auto;
    padding: 0 15px 24px;
    div.back-button {
      background: white;
      padding: 12px 0;
      height: 40px;
      span {
        cursor: pointer
      }
    }
  }

  .stick-btnGroup.stickyBtnClass {
    ::v-deep .hkl-header-back,
    ::v-deep .hkl-header-link {
      margin-top: 0;
      margin-bottom: 0;
    }
  }
</style>
